import React, { useEffect, useState } from "react";

const FilesViewer = ({ filepath, isFullscreen }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerSize, setContainerSize] = useState({
    width: isFullscreen ? "80vw" : "600px",
    height: isFullscreen ? "100vh" : "500px",
  });

  useEffect(() => {
    const updateSize = () => {
      setContainerSize({
        width: isFullscreen ? "80vw" : "600px",
        height: isFullscreen ? "100vh" : "500px",
      });
    };

    updateSize();

    // Cleanup function to reset to default size
    return () => {
      if (isFullscreen) {
        setContainerSize({
          width: "600px",
          height: "500px",
        });
      }
    };
  }, [isFullscreen]);

  if (!filepath) {
    return <p>No files available.</p>;
  }

  const files = filepath.split(";").map((file) => file.trim());

  const handleNext = () => {
    if (currentIndex < files.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prevIndex) => prevIndex - 1);
    }
  };

  const currentFile = files[currentIndex];
  const fileExtension = currentFile.split(".").pop()?.toLowerCase();

  return (
    <div
      style={{
        width: containerSize.width,
        height: containerSize.height,
        border: "2px solid #ccc",
        borderRadius: "12px",
        padding: "16px",
        backgroundColor: "#f9fafb",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* PDF Viewer */}
      {fileExtension === "pdf" && (
        <iframe
          src={currentFile}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: "8px",
          }}
          title={`PDF Viewer ${currentIndex}`}
        />
      )}

      {/* Image Viewer */}
      {["jpg", "jpeg", "png", "gif"].includes(fileExtension) && (
        <img
          src={currentFile}
          alt={`Evidence ${currentIndex}`}
          style={{
            maxWidth: "90%",
            maxHeight: "90%",
            objectFit: "contain",
            borderRadius: "8px",
          }}
        />
      )}

      {/* Office Docs Viewer */}
      {["doc", "docx", "xls", "xlsx", "pptx"].includes(fileExtension) && (
        <iframe
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(currentFile)}`}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            borderRadius: "8px",
          }}
          title={`Office Viewer ${currentIndex}`}
        />
      )}

      {/* Unsupported File */}
      {!["pdf", "jpg", "jpeg", "png", "gif", "doc", "docx", "xls", "xlsx", "pptx"].includes(fileExtension) && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <p style={{ fontSize: "18px", color: "#555", marginBottom: "16px" }}>
            This file type is not supported for preview. You can download it below.
          </p>
          <a
            href={currentFile}
            download
            style={{
              padding: "12px 24px",
              backgroundColor: "#007BFF",
              color: "white",
              fontSize: "16px",
              borderRadius: "8px",
              textDecoration: "none",
            }}
          >
            Download File
          </a>
        </div>
      )}

      {/* Navigation Buttons */}
      {files.length > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "16px",
            width: "100%",
            gap: "12px",
          }}
        >
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            style={{
              padding: "12px 24px",
              backgroundColor: currentIndex === 0 ? "#ccc" : "#4CAF50",
              color: "white",
              fontSize: "16px",
              borderRadius: "8px",
              border: "none",
              cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              transition: "background 0.3s ease-in-out, transform 0.2s",
              transform: currentIndex === 0 ? "scale(1)" : "scale(1.05)",
            }}
          >
            ⬅ Previous
          </button>

          <span style={{ fontSize: "16px", color: "#555", alignSelf: "center" }}>
            File {currentIndex + 1} of {files.length}
          </span>

          <button
            onClick={handleNext}
            disabled={currentIndex === files.length - 1}
            style={{
              padding: "12px 24px",
              backgroundColor: currentIndex === files.length - 1 ? "#ccc" : "#007BFF",
              color: "white",
              fontSize: "16px",
              borderRadius: "8px",
              border: "none",
              cursor: currentIndex === files.length - 1 ? "not-allowed" : "pointer",
              transition: "background 0.3s ease-in-out, transform 0.2s",
              transform: currentIndex === files.length - 1 ? "scale(1)" : "scale(1.05)",
            }}
          >
            Next ➡
          </button>
        </div>
      )}
    </div>
  );
};

export default FilesViewer;
